// Copyright (C) 2021 Bystronic Laser AG

import { EChartsOption } from 'echarts';
import { FilterTimeAxisSpanEnum } from '../enums/FilterTimeAxisSpanEnum';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { ChartGenerator, ProcedureName } from './abstract/chartGenerator';
import { BendingPlanBendTimelineData } from './chartsData';
import { GeneratorParams } from './generatorParams';
import { metricsService } from '@/services/metrics.service';
import { Color as ColorEnum, getBendFlowSpeedColor } from '@/utils/color';
import Color from 'color';
import i18n from '@/i18n';
import {
  isBendSequenceRepeated,
  tooltipFormatter,
} from './bendingProductionPlanBendTimelineTooltipFormatter';

export default class BendingProductionPlanBendTimelineGenerator extends ChartGenerator<BendingPlanBendTimelineData> {
  constructor(procedure: ProcedureName, private readonly customerIdDh: number) {
    super(procedure);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
    timeAxisSpan?: FilterTimeAxisSpanEnum,
    params?: { [key: string]: any },
  ): Promise<BendingPlanBendTimelineData | null> {
    return await metricsService.getBendingDevicesMetrics(
      this.procedure,
      {
        deviceStringIds: selectedDevices,
        customerIdDh: this.customerIdDh,
        partGuid: params!.partGuid,
        intervalId: params!.intervalId,
      },
      this.controller,
    );
  }

  override updateOptions(
    data: BendingPlanBendTimelineData,
    parameters: GeneratorParams,
    prevOptions?: EChartsOption,
  ): EChartsOption {
    const xAxisMin = Math.min(
      ...data.bendData.map((bendItem) => +new Date(bendItem.timestamp)),
      ...data.flowIntervals.map(
        (flowInterval) => +new Date(flowInterval.startTimestamp),
      ),
    );
    const xAxisMax = Math.max(
      ...data.bendData.map((bendItem) => +new Date(bendItem.timestamp)),
      ...data.flowIntervals.map(
        (flowInterval) => +new Date(flowInterval.endTimestamp),
      ),
    );
    const { bendsPerPart } = parameters.paramValues;

    return {
      grid: {
        left: 30,
        right: 10,
        top: 20,
        bottom: 80,
      },
      xAxis: {
        type: 'time',
        min: xAxisMin,
        max: xAxisMax,
        axisLabel: {
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          tooltipFormatter(params, data, bendsPerPart),
      },
      dataZoom: [
        {
          type: 'slider',
          filterMode: 'weakFilter',
          xAxisIndex: [0, 1],
          bottom: 20,
        },
        {
          type: 'inside',
          filterMode: 'weakFilter',
          xAxisIndex: [0, 1],
        },
      ],
      series: [
        {
          data: data.bendData.map((entry, index) => ({
            value: [entry.timestamp, entry.bendNumber, entry.bendSequence],
            ...(isBendSequenceRepeated(index, entry, data, bendsPerPart) && {
              itemStyle: {
                color: ColorEnum.Red,
              },
            }),
          })),
          type: 'line',
          markArea: {
            z: 0,
            label: {
              show: false,
            },
            data: data.flowIntervals.map((flowInterval) => [
              {
                name: i18n
                  .t(`bend_flow_speed_status.${flowInterval.flowSpeed}`)
                  .toString(),
                xAxis: flowInterval.startTimestamp,
                itemStyle: {
                  color: Color(getBendFlowSpeedColor(flowInterval.flowSpeed))
                    .alpha(0.4)
                    .string(),
                },
              },
              {
                xAxis: flowInterval.endTimestamp,
              },
            ]),
          },
        },
      ],
    };
  }
}
